import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Download, Music, AlertCircle, RefreshCw } from 'lucide-react';
import { formatFileSize } from './attachmentUtils';
import { usePresignedFileUrl } from './usePresignedFileUrl';

interface AudioAttachmentProps {
  fileName: string;
  url: string;
  fileSize?: number | undefined;
  workspaceId?: string | undefined;
  fileId?: string | undefined;
}

export const AudioAttachment: React.FC<AudioAttachmentProps> = ({
  fileName,
  url,
  fileSize,
  workspaceId,
  fileId,
}) => {
  const [renderError, setRenderError] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const retryCountRef = useRef(0);
  const MAX_AUTO_RETRIES = 2;

  const presigned = usePresignedFileUrl(workspaceId, fileId);

  const displayUrl = presigned.url || url;
  const hasError = presigned.hasError || renderError;

  const backendDownloadUrl = (workspaceId && fileId)
    ? `/api/v1/workspaces/${workspaceId}/files/${fileId}/download?disposition=attachment`
    : displayUrl;

  // Bug 4 fix: Preserve audio playback position when presigned URL renews without resetting media element
  useEffect(() => {
    if (audioRef.current && displayUrl && audioRef.current.src !== displayUrl) {
      const currentTime = audioRef.current.currentTime;
      const isPaused = audioRef.current.paused;
      audioRef.current.src = displayUrl;
      if (currentTime > 0) {
        audioRef.current.currentTime = currentTime;
      }
      if (!isPaused) {
        audioRef.current.play().catch(() => {});
      }
    }
  }, [displayUrl]);

  // Bug 3 fix: AudioAttachment automatically refreshes presigned URL on error
  const handleAudioError = useCallback(() => {
    if (retryCountRef.current < MAX_AUTO_RETRIES && workspaceId && fileId) {
      retryCountRef.current += 1;
      console.info('[AudioAttachment] Audio load error — auto-refreshing presigned URL, attempt', retryCountRef.current);
      setRenderError(false);
      presigned.refresh(true);
    } else {
      setRenderError(true);
    }
  }, [workspaceId, fileId, presigned]);

  const handleRetry = async () => {
    retryCountRef.current = 0;
    setRenderError(false);
    await presigned.refresh(true);
  };

  return (
    <div className="my-2 max-w-md select-none">
      <div className="p-3 rounded-2xl border border-border/80 bg-card shadow-sm space-y-2">
        <div className="flex items-center justify-between gap-2.5">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center flex-shrink-0">
              <Music className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-foreground truncate text-xs">{fileName}</p>
              {(fileSize || presigned.fileSize > 0) && (
                <p className="text-[11px] text-muted-foreground">{formatFileSize(fileSize || presigned.fileSize)}</p>
              )}
            </div>
          </div>

          <a
            href={backendDownloadUrl}
            download={fileName}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-xl hover:bg-primary/10 text-primary transition flex-shrink-0"
            title="Download audio"
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="h-4 w-4" />
          </a>
        </div>

        {/* Audio element */}
        {hasError ? (
          <div className="flex items-center justify-between gap-2 pt-1">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
              Audio preview unavailable
            </span>
            <button
              type="button"
              onClick={handleRetry}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-muted hover:bg-muted/80 text-foreground text-xs font-semibold transition"
            >
              <RefreshCw className="h-3 w-3" />
              Retry
            </button>
          </div>
        ) : (
          <audio ref={audioRef} controls onError={handleAudioError} className="w-full h-8 rounded-lg">
            <source src={displayUrl} />
            Your browser does not support the audio element.
          </audio>
        )}
      </div>
    </div>
  );
};
